// - - - - - - - - - -
// #173 — Hard-surface partial-capacity engine. The hard-surface analog of
// `computeRemainingCarpetCapacity`, extended to the 7 capacity TYPES Brandon
// mapped (VinylPlank, VinylSheet, Tile, Backsplash, Floating, GlueDown,
// NailDown — see HARD_SURFACE_CAPACITY_TYPES in keys.js).
//
// Design (confirmed with Leland + Brandon, 2026-06-02):
//   • IGNORE `doc.surface`. A job's TYPE is decided by which type's MAIN SKU
//     list its InvoiceLineItems match. A job can contribute to more than one
//     type if its line items span types — each type is evaluated independently.
//   • UNIT = square feet. A matched main-SKU line item's `qty` IS the job's
//     sqft for that type (verified live: inv 2209365 sku 503247 qty 266 ==
//     Wood Materials total_sqft 266). Same SKU→qty summation carpet uses for
//     furniture/steps. NO ÷9 conversion (capacity_<field>_* are already sqft).
//   • INDEPENDENT PER TYPE, mirroring carpet's #167 per-stop-cap + daily-budget
//     model. Each type has capacity_<field>_once/twice (sqft) plus
//     _once/_twice_furniture and _once/_twice_steps sub-caps, fed by the type's
//     furniture/step SKU lists.
//   • STOPS (capacity_stops_jobs / capacity_stops_repairs) are surface-agnostic
//     per-day caps — counted ONCE across all of the installer's jobs that day,
//     exactly like the carpet path. The #171 follow-up gate applies: the cell
//     only fires on a type's main capacity when a job-stop is actually free.
//
// The cell-flag decision (hasPartialCapacity / hasYardagePartialCapacity) is
// the OR across the installer's configured types, so a Hard Surface crew lights
// up if ANY of their types has room for another job (or a repair fits). The
// per-type breakdown is returned for the recommender + popover.

import {
  dateInJobRange,
  sumLineItemQtyForSkuList,
} from "./installerCapacityRemaining";
import { HARD_SURFACE_CAPACITY_TYPES } from "./keys";

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

/**
 * #167 per-dimension capacity math (shared shape with carpet). Given the
 * once/twice caps, the count of jobs in this dimension, the used total, and
 * the largest single job, returns the effective remaining capacity for the
 * NEXT slot the recommender would fill.
 *   count = 0          → next job is the "main" → bounded by safeOnce
 *   count ≥ 1 eligible → daily budget = safeTwice × 2; remaining = budget − used
 *   count ≥ 1 not elig → "one large" mode locked → 0
 * Eligibility: the main job must fit the daily budget (maxJob ≤ safeTwice × 2).
 */
const effectiveRemaining = (safeOnce, safeTwice, count, used, maxJob) => {
  if (count === 0) return safeOnce;
  const eligible = maxJob <= safeTwice * 2;
  return eligible ? Math.max(0, safeTwice * 2 - used) : 0;
};

/**
 * Computes hard-surface capacity remaining for one installer on one date,
 * across all 7 capacity types.
 *
 * @param {Object}   params
 * @param {Object}   params.candidate         — installer Candidate doc
 * @param {string}   params.dateString        — "M/D/YYYY" target date
 * @param {Array}    params.activeQueueDocs    — raw ActiveQueue docs (Materials +
 *                                              InvoiceLineItems intact)
 * @param {Function} [params.normalizePayroll] — payroll formatter (match installer)
 * @returns {{
 *   hasPartialCapacity: boolean,        // → 🔍 icon
 *   hasYardagePartialCapacity: boolean, // → red border (named to match the
 *                                       //   carpet field so OptimizerGrid reads
 *                                       //   one flag; "yardage" here means "a
 *                                       //   type has main-sqft room for a job")
 *   isHardSurface: true,
 *   unit: "sqft",
 *   usedJobStops: number, usedRepairStops: number,
 *   remainingJobStops: number, remainingRepairStops: number,
 *   isStopsJobsCapConfigured: boolean, isStopsRepairsCapConfigured: boolean,
 *   perType: Array<{ type, capacityField, count, usedSqft, remaining,
 *                    effectiveCap, remainingFurniture, remainingSteps,
 *                    canTakeJob }>,
 * }}
 */
export function computeRemainingHardSurfaceCapacity({
  candidate,
  dateString,
  activeQueueDocs,
  normalizePayroll,
}) {
  const empty = {
    hasPartialCapacity: false,
    hasYardagePartialCapacity: false,
    isHardSurface: true,
    unit: "sqft",
    usedJobStops: 0,
    usedRepairStops: 0,
    remainingJobStops: Infinity,
    remainingRepairStops: Infinity,
    isStopsJobsCapConfigured: false,
    isStopsRepairsCapConfigured: false,
    perType: [],
  };
  if (!candidate || !dateString) return empty;
  if (!Array.isArray(activeQueueDocs)) return { ...empty };

  // - - - stops caps (surface-agnostic; identical semantics to carpet)
  const rawCapStopsJobs = candidate.capacity_stops_jobs;
  const rawCapStopsRepairs = candidate.capacity_stops_repairs;
  const parsedStopsJobs = parseFloat(rawCapStopsJobs);
  const parsedStopsRepairs = parseFloat(rawCapStopsRepairs);
  const isStopsJobsCapConfigured =
    rawCapStopsJobs !== undefined &&
    rawCapStopsJobs !== null &&
    !isNaN(parsedStopsJobs);
  const isStopsRepairsCapConfigured =
    rawCapStopsRepairs !== undefined &&
    rawCapStopsRepairs !== null &&
    !isNaN(parsedStopsRepairs);
  const safeStopsJobs = isStopsJobsCapConfigured ? parsedStopsJobs : 0;
  const safeStopsRepairs = isStopsRepairsCapConfigured ? parsedStopsRepairs : 0;

  const normalize = normalizePayroll || ((v) => v);
  const installerPayroll = normalize(candidate.payrollNo);

  // - - - gather this installer's docs covering the date (one pass), counting
  // surface-agnostic stops AND building per-type accumulators.
  const acc = HARD_SURFACE_CAPACITY_TYPES.map((t) => ({
    ...t,
    safeOnce: num(candidate[`capacity_${t.capacityField}_once`]),
    safeTwice: num(candidate[`capacity_${t.capacityField}_twice`]),
    safeOnceFurniture: num(candidate[`capacity_${t.capacityField}_once_furniture`]),
    safeTwiceFurniture: num(candidate[`capacity_${t.capacityField}_twice_furniture`]),
    safeOnceSteps: num(candidate[`capacity_${t.capacityField}_once_steps`]),
    safeTwiceSteps: num(candidate[`capacity_${t.capacityField}_twice_steps`]),
    count: 0,
    usedSqft: 0,
    usedFurniture: 0,
    usedSteps: 0,
    maxJobSqft: 0,
    maxJobFurniture: 0,
    maxJobSteps: 0,
  }));

  let usedJobStops = 0;
  let usedRepairStops = 0;

  for (const doc of activeQueueDocs) {
    if (!doc) continue;
    if (normalize(doc.installer) !== installerPayroll) continue;
    if (!dateInJobRange(dateString, doc.date_scheduled, doc.date_estimatedCompletion)) continue;
    if (parseFloat(doc.statusCode) === 20) continue; // #169 — exclude only Cancelled

    // surface-agnostic stops (mirrors carpet)
    if (doc.classification === "Repair Order") usedRepairStops += 1;
    else usedJobStops += 1;

    // per-type sqft/furniture/steps — a job counts for type T if it has any
    // main-SKU line item for T; its `qty` sum is the sqft for that type.
    for (const t of acc) {
      const sqft = sumLineItemQtyForSkuList(doc, t.mainSkus);
      if (sqft <= 0) continue;
      t.count += 1;
      t.usedSqft += sqft;
      if (sqft > t.maxJobSqft) t.maxJobSqft = sqft;
      const furn = sumLineItemQtyForSkuList(doc, t.furnitureSkus);
      const steps = sumLineItemQtyForSkuList(doc, t.stepSkus);
      t.usedFurniture += furn;
      t.usedSteps += steps;
      if (furn > t.maxJobFurniture) t.maxJobFurniture = furn;
      if (steps > t.maxJobSteps) t.maxJobSteps = steps;
    }
  }

  const remainingJobStops = isStopsJobsCapConfigured
    ? Math.max(0, safeStopsJobs - usedJobStops)
    : Infinity;
  const remainingRepairStops = isStopsRepairsCapConfigured
    ? Math.max(0, safeStopsRepairs - usedRepairStops)
    : Infinity;

  // - - - per-type effective remaining + can-take-a-job
  const anyWorkToday = usedJobStops + usedRepairStops >= 1;
  let anyTypeCanTakeJob = false;
  // #192 follow-up (Brandon, 2026-06-10 — rule A): a TYPE is over capacity when
  // its day's used sqft exceeds its max daily capacity (twice-mode budget =
  // safeTwice×2, else one-load = safeOnce). Brandon: an installer over capacity
  // on ANY surface they work is FULL for the day — even on their other
  // surfaces. Tracked here and used to suppress this engine's flags below, and
  // OR'd across engines in computeCellCapacityInfo for does-both installers.
  let anyTypeOverCapacity = false;
  const perType = acc
    // only types the installer is actually qualified for (some cap configured)
    .filter((t) => t.safeOnce > 0 || t.safeTwice > 0)
    .map((t) => {
      const typeDailyMax = t.safeTwice > 0 ? t.safeTwice * 2 : t.safeOnce;
      const isOverCapacity = t.count >= 1 && t.usedSqft > typeDailyMax;
      if (isOverCapacity) anyTypeOverCapacity = true;
      const effectiveCap = effectiveRemaining(
        t.safeOnce,
        t.safeTwice,
        t.count,
        t.usedSqft,
        t.maxJobSqft
      );
      const remainingFurniture = effectiveRemaining(
        t.safeOnceFurniture,
        t.safeTwiceFurniture,
        t.count,
        t.usedFurniture,
        t.maxJobFurniture
      );
      const remainingSteps = effectiveRemaining(
        t.safeOnceSteps,
        t.safeTwiceSteps,
        t.count,
        t.usedSteps,
        t.maxJobSteps
      );
      // #171 follow-up: a job can only be added to this type if main capacity
      // remains AND a job-stop is free (job-stops are the shared per-day cap).
      const canTakeJob =
        anyWorkToday &&
        effectiveCap > 0 &&
        remainingJobStops > 0;
      if (canTakeJob) anyTypeCanTakeJob = true;
      return {
        type: t.type,
        capacityField: t.capacityField,
        count: t.count,
        usedSqft: t.usedSqft,
        effectiveCap,
        remaining: effectiveCap,
        remainingFurniture,
        remainingSteps,
        canTakeJob,
        isOverCapacity,
      };
    });

  // Cell flags (OR across types), mirroring carpet's structure + the #171
  // repair-only / #171-follow-up job-stop rules:
  //   border (hasYardagePartialCapacity) → some type can take a JOB
  //   icon   (hasPartialCapacity)        → some type can take a job, OR a
  //                                        repair fits (repair-stops free)
  //
  // #192 follow-up (rule A): if ANY type is over capacity, suppress the
  // FLOORING (yardage) flag so the installer's OTHER hard types don't light up
  // red / recommend. Repairs stay reachable (per #140) — the repair branch of
  // hasPartialCapacity is NOT gated by over-capacity.
  const hasYardagePartialCapacity = anyTypeCanTakeJob && !anyTypeOverCapacity;
  const hasPartialCapacity =
    anyWorkToday &&
    ((anyTypeCanTakeJob && !anyTypeOverCapacity) ||
      (isStopsRepairsCapConfigured && remainingRepairStops > 0));

  return {
    hasPartialCapacity,
    hasYardagePartialCapacity,
    isOverCapacity: anyTypeOverCapacity,
    isHardSurface: true,
    unit: "sqft",
    usedJobStops,
    usedRepairStops,
    remainingJobStops,
    remainingRepairStops,
    effectiveStopsJobsCap: safeStopsJobs,
    effectiveStopsRepairsCap: safeStopsRepairs,
    isStopsJobsCapConfigured,
    isStopsRepairsCapConfigured,
    perType,
  };
}
